import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  FileInfo,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Language } from "../subsidiaries/types";
import {
  AdminCarDriver,
  AssignNewDriver,
  CarDriver,
  CarDriverNoCarDepency,
  CarDriverVersion,
  CreateCarDriverDelegationBody,
  UpsertCarDriver,
} from "./types";

export const useCarDriversApi = () => {
  const api = useApi();

  const getAdminCarDriver = useCallback(
    (carDriverId: string, signal: AbortSignal) => {
      const url = `/admin/car_drivers/${carDriverId}`;
      return useSWRMutation<AdminCarDriver | null, CustomError, string>(url, (url) =>
        api.get<AdminCarDriver>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCurrentDriver = useCallback(() => {
    const url = `/admin/cars/{0}/current_driver`;
    return useSWRMutation<CarDriver, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
      api.get<CarDriver>({ url: url.replace("{0}", arg) }),
    );
  }, [api]);

  const useAdminCarDrivers = useCallback(
    <K extends keyof CarDriverNoCarDepency>(
      query: PaginationQueryWithFields<NestedPick<CarDriverNoCarDepency, K>>,
      signal: AbortSignal,
      carId?: string | false,
    ) => {
      const url =
        carId === false
          ? null
          : buildUrl<NestedPick<CarDriverNoCarDepency, K>>(
              carId ? `/admin/cars/${carId}/drivers` : "/admin/car_drivers",
              query,
            );
      return useSWR<Paginated<NestedPick<CarDriverNoCarDepency, K>> | null, CustomError, string | null>(url, (url) =>
        api.get<Paginated<NestedPick<CarDriverNoCarDepency, K>>>({ url: url!, signal }),
      );
    },
    [api],
  );

  const createDelegation = useCallback(
    async (carDriverId: string, carId: string, body: CreateCarDriverDelegationBody) => {
      const url = `/admin/car_drivers/${carDriverId}/delegation`;
      const result = await api.post<CreateCarDriverDelegationBody, { id: string }>(url, body);
      api.mutateCache(["/admin/car_documents", `/admin/cars/${carId}/documents`]);
      return result;
    },
    [api],
  );

  const insertAdminCarDriver = useCallback(
    async (body: UpsertCarDriver): Promise<{ id: string }> => {
      const url = `/admin/car_drivers`;
      const id = await api.post<UpsertCarDriver, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/cars/${body.carId}/car_drivers`,
        `/admin/cars/${body.carId}/drivers`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/overview`,
        `/admin/due_dates`,
        `/admin/cars/${body.carId}/tasks`,
        `/admin/car_plannings`,
        `/admin/car_tasks/status`,
        `/admin/car_plannings/${body.carPlanningId}`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminCarDriver = useCallback(
    async (id: string, body: UpsertCarDriver) => {
      const url = `/admin/car_drivers/${id}`;
      await api.put<UpsertCarDriver>(url, body);
      api.mutateCache([
        `/admin/car_drivers`,
        `/admin/cars/${body.carId}/drivers`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/overview`,
        `/admin/due_dates`,
        `/admin/car_drivers/${id}/versions`,
        `/admin/cars/${body.carId}/tasks`,
        `/admin/car_tasks/status`,
      ]);
    },
    [api],
  );

  const deleteAdminCarDriver = useCallback(
    async (carDriverId: string, carId: string): Promise<void> => {
      const url = `/admin/car_drivers/${carDriverId}`;
      await api.del(url).then(() => {
        api.mutateCache([
          `/admin/car_drivers`,
          `/admin/cars/${carId}/drivers`,
          `/admin/due_dates`,
          `/admin/cars/${carId}`,
          `/admin/cars/${carId}/overview`,
          `/admin/cars/${carId}/car_drivers`,
          `/admin/cars/${carId}/tasks`,
          `/admin/car_tasks/status`,
        ]);
      });
    },
    [api],
  );

  const assignNewDriver = useCallback(
    async (body: AssignNewDriver) => {
      const url = `/admin/cars/${body.carId}/assign_driver`;
      await api.post<AssignNewDriver, { id: string }>(url, body);
      api.mutateCache([
        `/admin/cars`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/drivers`,
        `/admin/cars/${body.carId}/overview`,
        `/admin/cars/${body.carId}/tasks`,
        `/admin/car_tasks/status`,
        `/admin/car_tasks`,
        `/admin/car_drivers`,
        `/admin/car_plannings/${body.carPlanningId}`,
      ]);
    },
    [api],
  );

  const getAdminCarDriverHistory = useCallback(
    (query: PaginationQuery, carDriverID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/car_drivers/${carDriverID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCarDriverVersion = useCallback(
    (carDriverId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/car_drivers/${carDriverId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CarDriverVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CarDriverVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/car_drivers/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const exportAgreement = useCallback(
    (id: string, language: Language, signal: AbortSignal) => {
      const url = `/admin/car_drivers/${id}/agreement`;
      const h = new Headers();
      h.append("Language", language.value);
      const r = api.get<FileInfo>({ url, signal, headers: h });
      return r;
    },
    [api],
  );

  const exportExcel = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/car_drivers", query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  return {
    deleteAdminCarDriver,
    updateAdminCarDriver,
    insertAdminCarDriver,
    useAdminCarDrivers,
    getAdminCarDriver,
    getAdminCurrentDriver,
    createDelegation,
    assignNewDriver,
    getAdminCarDriverHistory,
    getAdminCarDriverVersion,
    getFieldFilters,
    exportAgreement,
    exportExcel,
  };
};
